import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dropdown,
  Icon,
  Label,
  Message,
} from 'semantic-ui-react';
import {
  organizationModelRowsV104,
  setOrganizationModelExcludedV104,
  setOrganizationModelsExcludedV1013,
} from './organizationModelsV104';
import './organizationModelsV104.css';

const EMPTY_MODELS = [];

export default function OrganizationModelExclusionsV104({
  models,
  knownModels = EMPTY_MODELS,
  exclusions,
  mode = 'rules',
  loading,
  error,
  disabled,
  onRefresh,
  onChange,
  onModeChange,
  selectionScope = '',
}) {
  const { t } = useTranslation();
  const [queries, setQueries] = useState({ excluded: '', available: '' });
  const [selecting, setSelecting] = useState({
    excluded: false,
    available: false,
  });
  const [selections, setSelections] = useState({ excluded: [], available: [] });
  const locked = Boolean(disabled || loading || error);
  useEffect(() => {
    setSelecting({ excluded: false, available: false });
    setSelections({ excluded: [], available: [] });
  }, [selectionScope, mode]);
  useEffect(() => {
    if (disabled || error || mode !== 'rules') {
      setSelecting({ excluded: false, available: false });
      setSelections({ excluded: [], available: [] });
      return;
    }
    if (loading) return;
    // Do not retain a selection after its model leaves the list or group scope.
    setSelections((current) => {
      const next = {};
      for (const key of ['excluded', 'available']) {
        const rows = new Set(
          organizationModelRowsV104({
            routableModels: models,
            knownModels,
            excludedModels: exclusions,
            excluded: key === 'excluded',
          })
        );
        next[key] = current[key].filter((model) => rows.has(model));
      }
      return next.excluded.length === current.excluded.length &&
        next.available.length === current.available.length
        ? current
        : next;
    });
  }, [models, knownModels, exclusions, disabled, loading, error, mode]);
  const routable = new Set(models);
  const modelName = (model) =>
    routable.has(model)
      ? model
      : `${model} (${t('organization.fields.model_unavailable')})`;
  const hasModels =
    models.length || knownModels.some((model) => !exclusions.includes(model));
  const states = [
    {
      key: 'excluded',
      excluded: true,
      heading: 'excluded_tab',
      search: 'search_to_exclude',
      action: 'restore_model',
      list: 'excluded_models',
    },
    {
      key: 'available',
      excluded: false,
      heading: 'available_tab',
      search: 'search_to_allow',
      action: 'exclude_model',
      list: 'allowed_models',
    },
  ];
  const emptyMessage = (query, excluded) =>
    t(
      `organization.fields.${
        loading
          ? 'models_loading'
          : error
          ? 'models_load_error'
          : !hasModels
          ? 'no_group_models'
          : query.trim()
          ? 'no_matching_models'
          : excluded
          ? 'no_excluded_models'
          : 'no_available_models'
      }`
    );
  return (
    <div className='organization-model-editor-v104' aria-busy={loading}>
      <div className='organization-model-mode-v104'>
        <div className='organization-model-mode-options-v104'>
          <span>{t('organization.fields.all_group_models')}</span>
          <Checkbox
            id='organization-model-mode-switch-v104'
            toggle
            checked={mode === 'rules'}
            label={t('organization.fields.model_rules')}
            disabled={locked}
            onChange={(_, { checked }) =>
              onModeChange(checked ? 'rules' : 'all_current')
            }
          />
        </div>
        <Button
          type='button'
          icon
          title={t('organization.actions.refresh_models')}
          aria-label={t('organization.actions.refresh_models')}
          disabled={disabled || loading}
          loading={loading}
          onClick={onRefresh}
        >
          <Icon name='refresh' />
        </Button>
      </div>
      {error && (
        <Message
          negative
          size='small'
          content={t('organization.fields.models_load_error')}
        />
      )}
      {mode === 'rules' &&
        states.map((state) => {
          const query = queries[state.key];
          const destination = organizationModelRowsV104({
            routableModels: models,
            knownModels,
            excludedModels: exclusions,
            excluded: state.excluded,
          });
          const visible = organizationModelRowsV104({
            routableModels: models,
            knownModels,
            excludedModels: exclusions,
            excluded: state.excluded,
            query,
          });
          const candidates = organizationModelRowsV104({
            routableModels: models,
            knownModels,
            excludedModels: exclusions,
            excluded: !state.excluded,
          });
          const move = (model, excluded) => {
            if (!locked)
              onChange(
                setOrganizationModelExcludedV104(exclusions, model, excluded)
              );
          };
          const selected = selections[state.key].filter((model) =>
            destination.includes(model)
          );
          const unavailable = destination.filter(
            (model) => !routable.has(model)
          );
          const moveMany = (targets) => {
            if (locked || !targets.length) return;
            onChange(
              setOrganizationModelsExcludedV1013(
                exclusions,
                targets,
                !state.excluded
              )
            );
            setSelections((current) => ({ ...current, [state.key]: [] }));
          };
          return (
            <section
              key={state.key}
              className='organization-model-section-v104'
              aria-labelledby={`organization-${state.key}-heading-v104`}
            >
              <h4 id={`organization-${state.key}-heading-v104`}>
                {t(`organization.fields.${state.heading}`, {
                  count: destination.length,
                })}
              </h4>
              <div
                className='organization-model-actions-v1013'
                role='group'
                aria-label={t(`organization.fields.${state.heading}`, {
                  count: destination.length,
                })}
              >
                <Button
                  type='button'
                  size='mini'
                  basic
                  data-action='remove-unavailable'
                  disabled={locked || !unavailable.length}
                  onClick={() => moveMany(unavailable)}
                >
                  <Icon name='unlink' />
                  {t('organization.actions.remove_unavailable_models', {
                    count: unavailable.length,
                  })}
                </Button>
                <Button
                  type='button'
                  size='mini'
                  basic
                  data-action='remove-all'
                  disabled={locked || !destination.length}
                  onClick={() => moveMany(destination)}
                >
                  <Icon name='trash alternate outline' />
                  {t('organization.actions.remove_all_models', {
                    count: destination.length,
                  })}
                </Button>
                <Button
                  type='button'
                  size='mini'
                  basic
                  data-action='multi-remove'
                  aria-pressed={selecting[state.key]}
                  disabled={
                    locked || (!selecting[state.key] && !destination.length)
                  }
                  onClick={() => {
                    setSelecting((current) => ({
                      ...current,
                      [state.key]: !current[state.key],
                    }));
                    setSelections((current) => ({
                      ...current,
                      [state.key]: [],
                    }));
                  }}
                >
                  <Icon
                    name={
                      selecting[state.key] ? 'close' : 'check square outline'
                    }
                  />
                  {t(
                    `organization.actions.${
                      selecting[state.key]
                        ? 'cancel_model_selection'
                        : 'multi_remove_models'
                    }`
                  )}
                </Button>
              </div>
              {selecting[state.key] && (
                <div className='organization-model-selection-v1013'>
                  <Checkbox
                    label={t('organization.actions.select_visible_models', {
                      count: visible.length,
                    })}
                    checked={
                      visible.length > 0 &&
                      visible.every((model) => selected.includes(model))
                    }
                    indeterminate={
                      visible.some((model) => selected.includes(model)) &&
                      !visible.every((model) => selected.includes(model))
                    }
                    disabled={locked || !visible.length}
                    onChange={(_, { checked }) => {
                      if (locked) return;
                      setSelections((current) => ({
                        ...current,
                        [state.key]: checked
                          ? [...new Set([...selected, ...visible])]
                          : selected.filter(
                              (model) => !visible.includes(model)
                            ),
                      }));
                    }}
                  />
                  <Button
                    type='button'
                    size='mini'
                    basic
                    icon
                    data-action='clear-selection'
                    title={t('organization.actions.clear_model_selection')}
                    aria-label={t('organization.actions.clear_model_selection')}
                    disabled={locked || !selected.length}
                    onClick={() =>
                      setSelections((current) => ({
                        ...current,
                        [state.key]: [],
                      }))
                    }
                  >
                    <Icon name='eraser' />
                  </Button>
                  <Button
                    type='button'
                    size='mini'
                    basic
                    data-action='remove-selected'
                    disabled={locked || !selected.length}
                    onClick={() => moveMany(selected)}
                  >
                    <Icon name='minus circle' />
                    {t('organization.actions.remove_selected_models', {
                      count: selected.length,
                    })}
                  </Button>
                </div>
              )}
              <Dropdown
                className={`organization-model-picker-v104 organization-${state.key}-picker-v104`}
                fluid
                selection
                search={(options, value) =>
                  options.filter((option) =>
                    option.value
                      .toLowerCase()
                      .includes(value.trim().toLowerCase())
                  )
                }
                value=''
                open={selecting[state.key] ? false : undefined}
                searchQuery={query}
                selectOnBlur={false}
                selectOnNavigation={false}
                searchInput={{
                  'aria-label': t(
                    `organization.fields.${
                      selecting[state.key] ? 'search_models' : state.search
                    }`
                  ),
                }}
                placeholder={t(
                  `organization.fields.${
                    selecting[state.key] ? 'search_models' : state.search
                  }`
                )}
                disabled={locked}
                loading={loading}
                options={candidates.map((model) => ({
                  key: model,
                  value: model,
                  text: modelName(model),
                  icon: 'plus',
                }))}
                onSearchChange={(_, { searchQuery }) =>
                  setQueries((current) => ({
                    ...current,
                    [state.key]: searchQuery,
                  }))
                }
                onChange={(_, { value }) => {
                  if (!selecting[state.key] && candidates.includes(value))
                    move(value, state.excluded);
                }}
                noResultsMessage={t('organization.fields.no_matching_models')}
              />
              <div
                className={`organization-model-tags-v104 organization-${state.key}-tags-v104`}
                role='list'
                aria-label={t(`organization.fields.${state.list}`)}
                tabIndex={0}
              >
                {visible.map((model) => (
                  <Label
                    basic
                    as='span'
                    role='listitem'
                    key={model}
                    data-model={state.excluded ? model : undefined}
                    data-available-model={state.excluded ? undefined : model}
                  >
                    {selecting[state.key] && (
                      <input
                        type='checkbox'
                        className='organization-model-checkbox-v1013'
                        aria-label={t('organization.actions.select_model', {
                          model,
                        })}
                        checked={selected.includes(model)}
                        disabled={locked}
                        onChange={(event) => {
                          if (locked) return;
                          const checked = event.target.checked;
                          setSelections((current) => ({
                            ...current,
                            [state.key]: checked
                              ? [...new Set([...selected, model])]
                              : selected.filter((value) => value !== model),
                          }));
                        }}
                      />
                    )}
                    <span className='organization-model-name-v104'>
                      {modelName(model)}
                    </span>
                    <button
                      type='button'
                      disabled={locked}
                      aria-label={`${t(
                        `organization.actions.${state.action}`
                      )} ${model}`}
                      title={`${t(
                        `organization.actions.${state.action}`
                      )} ${model}`}
                      onClick={() => move(model, !state.excluded)}
                    >
                      <Icon name='close' />
                    </button>
                  </Label>
                ))}
                {!visible.length && (
                  <span role='status'>
                    {emptyMessage(query, state.excluded)}
                  </span>
                )}
              </div>
            </section>
          );
        })}
    </div>
  );
}
